/* eslint-disable max-lines-per-function */
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { isEmpty } from 'lodash-es';
import isEmail from 'validator/lib/isEmail';
import {
    palette,
    TertiaryButton,
    SaveTemplateIcon,
    SendTestIcon,
    WebsitePrimaryButton,
    ViewTemplateIcon,
    ReplaceTemplateIcon,
    EditTemplateIcon,
} from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { clone, now } from 'lodash-es';

import { ShowDialog, DisableDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { useCampaignScreensPathsChecker } from '@/components/CampaignCreator/hooks/useCampaignScreensPathsChecker';
import { getCampainCreatorStore, SendTest, SetCampaignErrors } from '@/store/campaignCreator/actions';
import { CampaignErrorNames } from '@/store/campaignCreator/types';
import {
    DeselectTemplate,
    EditCampaignTemplate,
    OpenPreviewOverlay,
    SaveInMyTemplates,
} from '@/store/campaignCreator/actions';
import { TemplatePreviewStart } from '@/store/templateList/actions';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import { useRouteMatch } from 'react-router';
import { pathsParams } from '@/services/dictionaries/Paths';
import { TemplatePreviewButton } from './components/TemplatePreviewButton';
import { TemplateAttachments } from './components/TemplateAttachments';
import { SendTestModal } from './components/SendTestModal';
import { SaveInMyTemplatesModal } from './components/SaveInMyTemplatesModal';

const ScreenshotWrapper = styled.div`
    height: 400px;
    border-radius: 20px;
    border: 1px solid ${palette.gray[2]};
    position: relative;

    img {
        width: 100%;
        height: 100%;
        border-radius: 20px;
        object-fit: cover;
        object-position: top;
    }
`;

const ScreenshotHover = styled.div`
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    top: 0;
    width: 100%;
    height: 400px;
    position: absolute;
    opacity: 0;
    border-radius: 20px;

    &:hover {
        transition: opacity 0.5s ease-in-out;
        opacity: 1;
        background-color: rgba(45, 47, 122, 0.24);
    },
`;

const SendTestButtonWrapper = styled.div`
    margin-top: 32px;
    svg {
        fill: ${palette.basic.paper};
    }
`;

const SaveButtonWrapper = styled.div`
    margin-top: 12px;
    svg {
        fill: ${palette.indigo[6]};
    }
`;
const isEmailInvalid = (email?: string): boolean => !email || !isEmail(email);

export const TemplatePreview = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { campaign, errors, activeTemplateIndex } = useSelector(getCampainCreatorStore);
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const campaignTemplate = campaign?.CampaignTemplates?.[activeTemplateIndex];

    const route = useRouteMatch<{ type: string }>();

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    const templateNameRef = useRef<string>();
    const fromNameRef = useRef<string>();
    const fromEmailRef = useRef<string>();
    const subjectRef = useRef<string>();

    const showDetailsAttachments =
        isDetailsScreen && !isEmpty(campaign?.CampaignTemplates[activeTemplateIndex]?.Attachments);
    const templateScreenshot = campaignTemplate?.Screenshot;

    const handleSaveTemplateConfirm = (): void => {
        const SaveTemplateParams = {
            templateName: templateNameRef.current,
            fromName: fromNameRef.current,
            fromEmail: fromEmailRef.current,
            subject: subjectRef.current,
        };
        dispatch(SaveInMyTemplates(SaveTemplateParams));
    };

    const cancelSaveInMyTemplatesErrors = () => {
        const errorsObject = clone(errors) || {};
        errorsObject[CampaignErrorNames.TEMPLATE_NAME_IN_USE] = false;
        dispatch(SetCampaignErrors(errorsObject));
    };

    const validateSaveTemplateForm = (): void => {
        let hasErrors = false;
        if (!templateNameRef.current) {
            hasErrors = true;
        }
        if (isEmailInvalid(fromEmailRef.current)) {
            hasErrors = true;
        }
        dispatch(DisableDialog(hasErrors));
    };

    const saveInTemplates = (): void => {
        templateNameRef.current = `${campaignTemplate?.TemplateName} ${now()}`;
        fromNameRef.current = campaignTemplate?.Sender?.FromName ?? '';
        fromEmailRef.current = campaignTemplate?.Sender?.From?.[0] ?? '';
        subjectRef.current = campaign?.Name ?? '';

        const dialogParams: ShowDialogParams = {
            title: t('savetemplates'),
            confirmLabel: t('save'),
            cancelLabel: t('cancel'),
            body: (
                <SaveInMyTemplatesModal
                    setTemplateName={(name: string) => {
                        templateNameRef.current = name;
                        validateSaveTemplateForm();
                    }}
                    setFromName={(fromName: string) => {
                        fromNameRef.current = fromName;
                    }}
                    setFromEmail={(fromEmail: string) => {
                        fromEmailRef.current = fromEmail;
                        validateSaveTemplateForm();
                    }}
                    setSubject={(subject: string) => {
                        subjectRef.current = subject;
                    }}
                    templateName={templateNameRef.current}
                    fromName={fromNameRef.current}
                    fromEmail={fromEmailRef.current}
                    subject={subjectRef.current}
                    isEmailInvalid={isEmailInvalid}
                />
            ),
            onClickConfirm: handleSaveTemplateConfirm,
            onClickCancel: cancelSaveInMyTemplatesErrors,
            isHiddenManually: true,
            isDisabled: isEmailInvalid(fromEmailRef.current),
        };
        dispatch(ShowDialog(dialogParams));
    };

    const viewTemplate = (): void => {
        dispatch(OpenPreviewOverlay());
        dispatch(TemplatePreviewStart(campaignTemplate?.TemplateName, false));
    };

    const editTemplate = (): void => {
        dispatch(EditCampaignTemplate(campaignTemplate?.TemplateName));
    };

    const cancelSendTestErrors = () => {
        const errorsObject = clone(errors) || {};
        errorsObject[CampaignErrorNames.NO_EMAIL_CREDITS] = false;
        errorsObject[CampaignErrorNames.SEND_TEST_TEMPLATE_SIZE] = false;
        errorsObject[CampaignErrorNames.SEND_TEST_INVALID_EMAILS] = false;
        errorsObject[CampaignErrorNames.SEND_TEST_INVALID_EMAILS_STATUS] = false;
        dispatch(SetCampaignErrors(errorsObject));
    };

    const sendTest = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('sendtest'),
            confirmLabel: t('sendtest'),
            cancelLabel: t('cancel'),
            body: <SendTestModal />,
            onClickConfirm: () => dispatch(SendTest()),
            onClickCancel: cancelSendTestErrors,
            isHiddenManually: true,
        };
        dispatch(ShowDialog(dialogParams));
    };

    const deselectTemplate = (): void => {
        dispatch(DeselectTemplate(activeTemplateIndex));
    };

    const replaceTemplate = (): void => {
        const dialogParams: ShowDialogParams = {
            title: t('replacetemplate'),
            confirmLabel: t('confirm'),
            body: t('replacetemplatedescription'),
            onClickConfirm: deselectTemplate,
            cancelLabel: t('cancel'),
        };
        dispatch(ShowDialog(dialogParams));
    };

    return (
        <Grid container xs={12} justifyContent="center" style={{ marginTop: 32 }}>
            <Grid item xs={6}>
                <ScreenshotWrapper>
                    <ScreenshotHover>
                        {!isDetailsScreen && (
                            <Grid item style={{ justifySelf: 'flex-end', marginTop: 24, marginRight: 24 }}>
                                <TemplatePreviewButton
                                    id={generateIdBasedOnPath('replace-template', routeForIds)}
                                    buttonIcon={<ReplaceTemplateIcon width={28.8} height={28.8} />}
                                    buttonLabel={t('replace')}
                                    handleOnClick={replaceTemplate}
                                />
                            </Grid>
                        )}
                        <Grid
                            container
                            style={{ marginTop: 24, marginBottom: 24, gridRow: isDetailsScreen && 2 }}
                            direction="row"
                            justifyContent="center"
                        >
                            <Grid item style={{ marginRight: !isDetailsScreen && 16 }}>
                                <TemplatePreviewButton
                                    id={generateIdBasedOnPath('view-template', routeForIds)}
                                    buttonIcon={<ViewTemplateIcon width={28.8} height={28.8} />}
                                    buttonLabel={t('view')}
                                    handleOnClick={viewTemplate}
                                />
                            </Grid>
                            {!isDetailsScreen && (
                                <TemplatePreviewButton
                                    id={generateIdBasedOnPath('edit-template', routeForIds)}
                                    buttonIcon={<EditTemplateIcon width={31.5} height={31.5} />}
                                    buttonLabel={t('edit')}
                                    handleOnClick={editTemplate}
                                />
                            )}
                        </Grid>
                    </ScreenshotHover>
                    <img src={`${APIPATH}/userfile${encodeURI(templateScreenshot)}`} />
                </ScreenshotWrapper>
                <SaveButtonWrapper>
                    <TertiaryButton
                        id={generateIdBasedOnPath('save-to-templates', routeForIds)}
                        onClick={saveInTemplates}
                        text={t('saveinmytemplates')}
                        startIcon={<SaveTemplateIcon />}
                    />
                </SaveButtonWrapper>
            </Grid>
            {(!isDetailsScreen || showDetailsAttachments) && (
                <Grid item xs={6} style={{ paddingLeft: 30 }}>
                    <TemplateAttachments />
                    {!isDetailsScreen && (
                        <SendTestButtonWrapper>
                            <WebsitePrimaryButton
                                id={generateIdBasedOnPath('save-test', routeForIds)}
                                text={t('sendtest')}
                                startIcon={<SendTestIcon width={27} height={27} />}
                                onClick={sendTest}
                                style={{ paddingTop: 8, paddingBottom: 8 }}
                            />
                        </SendTestButtonWrapper>
                    )}
                </Grid>
            )}
        </Grid>
    );
};
